import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { GlobalVariablesService } from '../../../core/services/global-variables.service';
import { Guid } from '../../../core/constants/constants';
import { CustomReportEditor } from './models/custom-report-editor.model';
import { UtilityService } from '@app/modules/live/services/utility.service';
import { SUPPRESS_ACCESS_DENIED_REDIRECT } from '@app/core/services/auth/guards/access-denied-redirect.context';

@Injectable()
export class CustomReportService {

    public customReports: CustomReportEditor.CustomReport[] = []; // Custom reports array

    constructor(
        private http: HttpClient,
        private varService: GlobalVariablesService,
        private utility: UtilityService) { }


    // Fetch user custom reports
    public getUserCustomReports(): Observable<CustomReportEditor.CustomReport[]> {
        const url = `${this.varService.apiV2Address}/reports/custom-reports`;
        return this.http.get<CustomReportEditor.CustomReport[]>(url, {
            context: new HttpContext().set(SUPPRESS_ACCESS_DENIED_REDIRECT, true)
        }).pipe(
            map((reports: CustomReportEditor.CustomReport[]) => {
                return reports.map((reportData) => this.hydrateReport(reportData));
            })
        );
    }
    // Add a new custom report
    public addCustomReport(report: CustomReportEditor.CustomReport): Observable<CustomReportEditor.CustomReport> {
        const url = `${this.varService.apiV2Address}/reports/custom-reports`;
        const reportToSend = this.serializeReport(report);
        return this.http.post<CustomReportEditor.CustomReport>(url, reportToSend).pipe(
            map((responseData) => this.hydrateReport(responseData))
        );
    }

    // Update an existing custom report
    public updateCustomReport(report: CustomReportEditor.CustomReport): Observable<CustomReportEditor.CustomReport> {
        const url = `${this.varService.apiV2Address}/reports/custom-reports/${report.id}`;
        const reportToSend = this.serializeReport(report);
        return this.http.put<CustomReportEditor.CustomReport>(url, reportToSend).pipe(
            map((responseData) => this.hydrateReport(responseData))
        );
    }

    // Delete a custom report
    public deleteCustomReport(reportId: Guid): Observable<CustomReportEditor.CustomReport> {
        const url = `${this.varService.apiV2Address}/reports/custom-reports/${reportId}`;
        return this.http.delete<CustomReportEditor.CustomReport>(url);
    }

    public getScheduleSlotPresets(options?: { filter?: string | null; targetType?: number | null }): Observable<any[]> {
        const url = `${this.varService.apiV2Address}/schedule-slot-presets`;
        let params = new HttpParams();

        if (options?.filter) {
            params = params.set('filter', options.filter);
        }

        if (options?.targetType !== null && options?.targetType !== undefined) {
            params = params.set('targetType', String(options.targetType));
        }

        return this.http.get<any[]>(url, { params });
    }

    public createScheduleSlotPreset(payload: any): Observable<any> {
        const url = `${this.varService.apiV2Address}/schedule-slot-presets`;
        return this.http.post<any>(url, payload);
    }

    public updateScheduleSlotPreset(presetId: Guid, payload: any): Observable<any> {
        const url = `${this.varService.apiV2Address}/schedule-slot-presets/${presetId}`;
        return this.http.put<any>(url, payload);
    }

    public deleteScheduleSlotPreset(presetId: Guid): Observable<void> {
        const url = `${this.varService.apiV2Address}/schedule-slot-presets/${presetId}`;
        return this.http.delete<void>(url);
    }

    public getReportWidgets(reportId?: Guid | string): Observable<CustomReportEditor.ReportWidget[]> {
        const url = `${this.varService.apiV2Address}/report-widgets`;
        let params = new HttpParams();

        if (reportId) {
            params = params.set('reportId', String(reportId));
        }

        return this.http.get<CustomReportEditor.ReportWidget[]>(url, { params });
    }

    public getReportWidgetTemplates(): Observable<CustomReportEditor.ReportWidgetTemplate[]> {
        const url = `${this.varService.apiV2Address}/report-widget-templates`;
        return this.http.get<CustomReportEditor.ReportWidgetTemplate[]>(url);
    }

    public previewReportWidget(payload: any, language?: string): Observable<CustomReportEditor.ReportWidgetData> {
        const url = `${this.varService.apiV2Address}/report-widgets/preview`;
        const params = this.buildLanguageParams(language);
        return this.http.post<CustomReportEditor.ReportWidgetData>(url, payload, { params });
    }

    public validateReportWidgetExpression(payload: any, language?: string): Observable<any> {
        const url = `${this.varService.apiV2Address}/report-widgets/expressions/validate`;
        const params = this.buildLanguageParams(language);
        return this.http.post<any>(url, payload, { params });
    }

    public createReportWidget(reportId: Guid | string, payload: any): Observable<CustomReportEditor.ReportWidget> {
        const url = `${this.varService.apiV2Address}/reports/custom-reports/${reportId}/widgets`;
        return this.http.post<CustomReportEditor.ReportWidget>(url, payload);
    }

    public updateReportWidget(widgetId: Guid | string, payload: any): Observable<CustomReportEditor.ReportWidget> {
        const url = `${this.varService.apiV2Address}/report-widgets/${widgetId}`;
        return this.http.put<CustomReportEditor.ReportWidget>(url, payload);
    }

    public deleteReportWidget(widgetId: Guid | string): Observable<void> {
        const url = `${this.varService.apiV2Address}/report-widgets/${widgetId}`;
        return this.http.delete<void>(url);
    }

    private buildLanguageParams(language?: string): HttpParams {
        const normalizedLanguage = String(language || '').trim();
        return normalizedLanguage ? new HttpParams().set('language', normalizedLanguage) : new HttpParams();
    }

    private hydrateReport(reportData: CustomReportEditor.CustomReport | any): CustomReportEditor.CustomReport {
        const report = Object.assign(new CustomReportEditor.CustomReport(), reportData);

        if (report && report.icon && typeof report.icon === 'string') {
            report.icon = this.utility.resolveIcon(report.icon);
        }

        if (reportData?.gridState) {
            report.gridState = reportData.gridState;
        }

        this.applyMailScheduleFields(report, reportData);
        this.attachMailScheduleDebug(report, reportData);
        return report;
    }

    private applyMailScheduleFields(report: CustomReportEditor.CustomReport, source?: any): void {
        const sourceData = source || {};
        const reportData = report as any;

        report.scheduleSlotPresetId = this.normalizeNullableString(
            this.resolveFirstMailScheduleValue(
                sourceData.scheduleSlotPresetId,
                sourceData.ScheduleSlotPresetId,
                reportData.scheduleSlotPresetId
            )
        ) as any;
        report.emailRecipients = this.normalizeEmailRecipients(
            this.resolveFirstMailScheduleValue(
                sourceData.emailRecipients,
                sourceData.EmailRecipients,
                reportData.emailRecipients
            )
        );
        report.emailFormat = this.normalizeEmailFormat(
            this.resolveFirstMailScheduleValue(
                sourceData.emailFormat,
                sourceData.EmailFormat,
                reportData.emailFormat
            )
        ) as any;
        report.emailLanguage = this.normalizeEmailLanguage(
            this.resolveFirstMailScheduleValue(
                sourceData.emailLanguage,
                sourceData.EmailLanguage,
                reportData.emailLanguage
            )
        );
        report.emailIncludeFilters = this.normalizeBooleanValue(
            this.resolveFirstMailScheduleValue(
                sourceData.emailIncludeFilters,
                sourceData.EmailIncludeFilters,
                reportData.emailIncludeFilters
            )
        );
    }

    private attachMailScheduleDebug(report: CustomReportEditor.CustomReport, source?: any): void {
        Object.defineProperty(report as any, '__mailScheduleMappingDebug', {
            value: {
                endpointRaw: this.buildMailScheduleDebugView(source),
                afterServiceHydrate: this.buildMailScheduleDebugView(report)
            },
            enumerable: false,
            configurable: true,
            writable: true
        });
    }

    // Helper method to serialize the report before sending to the server
    private serializeReport(report: CustomReportEditor.CustomReport | any): any {
        const {
            state,
            widgetEnabled,
            widgetId,
            widgetTopN,
            widgetTemplateKey,
            widgetTemplateConfigJson,
            widgetRefreshInterval,
            widgetHighlightFirst,
            widgetPublishToDashboard,
            widgetTitleColumn,
            widgetPrimaryColumn,
            widgetSecondaryColumn,
            widgetBadgeColumn,
            widgetColorColumn,
            ...reportWithoutState
        } = report;
        return {
            ...reportWithoutState,
            icon: report.icon ? JSON.stringify(report.icon) : null, // Convert IconProp to JSON string
            gridState: JSON.stringify(report.state), // Convert state object to JSON string
            scheduleSlotPresetId: this.normalizeNullableString(report.scheduleSlotPresetId),
            emailRecipients: this.normalizeEmailRecipients(report.emailRecipients),
            emailFormat: this.normalizeEmailFormat(report.emailFormat),
            emailLanguage: this.normalizeEmailLanguage(report.emailLanguage),
            emailIncludeFilters: this.normalizeBooleanValue(report.emailIncludeFilters)
            // state property çıkarıldı
        };
    }

    private resolveFirstMailScheduleValue(...values: unknown[]): unknown {
        return values.find((value) => value !== null && value !== undefined);
    }

    private normalizeNullableString(value: unknown): string | null {
        const normalized = String(value ?? '').trim();
        return normalized ? normalized : null;
    }

    private normalizeEmailRecipients(value: unknown): string {
        return String(value ?? '').trim();
    }

    private normalizeEmailFormat(value: unknown): number {
        if (value === null || value === undefined || String(value).trim() === '') {
            return 1;
        }

        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : 1;
    }

    private normalizeEmailLanguage(value: unknown): string {
        return String(value ?? '').trim() || 'en';
    }

    private normalizeBooleanValue(value: unknown): boolean {
        return value === true || String(value).trim().toLowerCase() === 'true';
    }

    private buildMailScheduleDebugView(source: unknown): any {
        const data = (source || {}) as Record<string, unknown>;
        return {
            rootKeys: Object.keys(data),
            topLevelPayload: this.buildTopLevelDebugPayload(data),
            identity: {
                id: data['id'] ?? data['Id'] ?? null,
                code: data['code'] ?? data['Code'] ?? null,
                name: data['name'] ?? data['Name'] ?? null,
                groupKey: data['groupKey'] ?? data['GroupKey'] ?? null
            },
            mailScheduleFields: this.buildMailScheduleDebugFields(data),
            gridState: this.describeGridStateForDebug(data['gridState'])
        };
    }

    private buildMailScheduleDebugFields(data: Record<string, unknown>): any {
        return {
            scheduleSlotPresetId: this.buildMailScheduleFieldDebug(data, 'scheduleSlotPresetId', 'ScheduleSlotPresetId'),
            emailRecipients: this.buildMailScheduleFieldDebug(data, 'emailRecipients', 'EmailRecipients'),
            emailFormat: this.buildMailScheduleFieldDebug(data, 'emailFormat', 'EmailFormat'),
            emailLanguage: this.buildMailScheduleFieldDebug(data, 'emailLanguage', 'EmailLanguage'),
            emailIncludeFilters: this.buildMailScheduleFieldDebug(data, 'emailIncludeFilters', 'EmailIncludeFilters')
        };
    }

    private buildMailScheduleFieldDebug(data: Record<string, unknown>, camelKey: string, pascalKey: string): any {
        return {
            selected: this.resolveFirstMailScheduleValue(data[camelKey], data[pascalKey]) ?? null,
            camelCase: data[camelKey] ?? null,
            pascalCase: data[pascalKey] ?? null
        };
    }

    private buildTopLevelDebugPayload(source: Record<string, unknown>): any {
        return Object.keys(source || {}).reduce((payload, key) => {
            if (key === 'gridState') {
                payload[key] = this.describeGridStateForDebug(source[key]);
                return payload;
            }

            if (key === 'state' && source[key] && typeof source[key] === 'object') {
                payload[key] = {
                    type: 'object',
                    rootKeys: Object.keys(source[key] as Record<string, unknown>)
                };
                return payload;
            }

            payload[key] = source[key];
            return payload;
        }, {} as Record<string, unknown>);
    }

    private describeGridStateForDebug(value: unknown): any {
        if (typeof value !== 'string') {
            return {
                type: typeof value,
                present: value !== null && value !== undefined
            };
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return {
                type: 'string',
                length: 0,
                parsedMailScheduleFields: null
            };
        }

        try {
            const parsed = JSON.parse(trimmed);
            return {
                type: 'string',
                length: trimmed.length,
                parsedRootKeys: parsed && typeof parsed === 'object' ? Object.keys(parsed) : [],
                parsedMailScheduleFields: this.pickMailScheduleDebugFields(parsed)
            };
        } catch {
            return {
                type: 'string',
                length: trimmed.length,
                parseError: true
            };
        }
    }

    private pickMailScheduleDebugFields(source: unknown): any {
        const data = (source || {}) as Record<string, unknown>;
        return {
            scheduleSlotPresetId: data['scheduleSlotPresetId'] ?? null,
            emailRecipients: data['emailRecipients'] ?? null,
            emailFormat: data['emailFormat'] ?? null,
            emailLanguage: data['emailLanguage'] ?? null,
            emailIncludeFilters: data['emailIncludeFilters'] ?? null
        };
    }
}
